import { isToolUIPart, getToolName, type UIMessage } from 'ai';
import { useState } from 'react';
import type { EmbedStrings } from '../strings';
import { fillTemplate } from '../strings';
import { Markdown } from './Markdown';
import { ToolConfirmCard } from './ToolConfirmCard';

export type PendingConfirm = {
  name: string;
  description?: string;
  inputSchema?: { properties?: Record<string, { description?: string; title?: string }> };
  input: unknown;
  resolve: (decision: 'confirm' | 'cancel') => void;
};

type Props = {
  message: UIMessage;
  /**
   * Tool calls awaiting user approval, keyed by toolCallId. When a part is
   * rendered in `input-available` state and its toolCallId is here, we show
   * a confirm card; otherwise we render a compact status line so the user
   * sees that something is happening even without a confirmation step
   * (autoRun=true tools).
   */
  pendingConfirms: Map<string, PendingConfirm>;
  strings: EmbedStrings;
};

export function MessageBubble({ message, pendingConfirms, strings }: Props) {
  const isUser = message.role === 'user';
  return (
    <div className={`kb-bubble ${isUser ? 'kb-bubble--user' : 'kb-bubble--assistant'}`}>
      {message.parts.map((part, i) => {
        if (part.type === 'text') {
          return (
            <div key={`text-${message.id}-${i}`} className="kb-bubble__content">
              {isUser ? part.text : <Markdown>{part.text}</Markdown>}
            </div>
          );
        }
        if (isToolUIPart(part)) {
          const tp = asToolPart(part);
          return (
            <ToolPartView
              key={`tool-${tp.toolCallId}`}
              part={part}
              pendingConfirms={pendingConfirms}
              strings={strings}
            />
          );
        }
        return null;
      })}
    </div>
  );
}

type ToolPart = {
  state: string;
  toolCallId: string;
  input: unknown;
  output?: unknown;
  errorText?: string;
  title?: string;
};

function asToolPart(p: unknown): ToolPart {
  return p as ToolPart;
}

function ToolPartView({
  part,
  pendingConfirms,
  strings,
}: {
  part: unknown;
  pendingConfirms: Map<string, PendingConfirm>;
  strings: EmbedStrings;
}) {
  const tp = asToolPart(part);
  const toolName = getToolName(part as Parameters<typeof getToolName>[0]);

  // Local "I clicked, awaiting unmount" flag: prevents double-clicks during
  // the gap between resolve() and the parent dropping us from pendingConfirms.
  const [busy, setBusy] = useState(false);

  if (tp.state === 'input-streaming' || tp.state === 'input-available') {
    const pending = pendingConfirms.get(tp.toolCallId);
    if (pending) {
      return (
        <ToolConfirmCard
          name={pending.name}
          description={pending.description}
          inputSchema={pending.inputSchema}
          input={pending.input}
          busy={busy}
          strings={strings}
          onConfirm={() => {
            setBusy(true);
            pending.resolve('confirm');
          }}
          onCancel={() => {
            setBusy(true);
            pending.resolve('cancel');
          }}
        />
      );
    }
    // Server-side filter sets `title` to the tool's evaluated label. When
    // present, prefer it over the raw toolName — protects against leaking
    // technical identifiers (KB slug, registry id) into the UI.
    if (tp.title) {
      return <div className="kb-tool-status">{tp.title}</div>;
    }
    return (
      <div className="kb-tool-status">
        {fillTemplate(strings.runningTool, { name: toolName })}
      </div>
    );
  }

  if (tp.state === 'output-available') {
    if (
      tp.output &&
      typeof tp.output === 'object' &&
      (tp.output as { _denied?: unknown })._denied === true
    ) {
      return <div className="kb-tool-status">{strings.cancelledTool}</div>;
    }
    // Filter projects post-execution UI as `output: { message: <string> }`.
    // Render verbatim — server is authoritative on phrasing per tool.
    const serverMessage =
      tp.output &&
      typeof tp.output === 'object' &&
      typeof (tp.output as { message?: unknown }).message === 'string'
        ? ((tp.output as { message: string }).message)
        : null;
    if (serverMessage) {
      return <div className="kb-tool-status kb-tool-status--done">{serverMessage}</div>;
    }
    return (
      <div className="kb-tool-status kb-tool-status--done">
        {fillTemplate(strings.doneTool, { name: toolName })}
      </div>
    );
  }

  if (tp.state === 'output-error') {
    // Don't surface raw internal error strings ("not registered for this
    // session", "bridge timeout") to non-technical users — they're
    // developer-facing. Show a single friendly prefix; the raw detail goes
    // into the browser console for debugging via the AI SDK.
    return (
      <div className="kb-tool-status kb-tool-status--error" role="alert">
        {strings.errorToolPrefix}
      </div>
    );
  }

  return null;
}
